/**
 * MKS-491 — dark mode.
 *
 * `useThemeController` (mounted once in Layout) applies the `dark` class on
 * <html> from the user's `preferences.colorScheme` and mirrors the choice to
 * localStorage for the pre-paint script in index.html. `useThemeChoice` backs
 * the header Theme menu.
 */
import { useEffect } from 'react';
import { usePreferences, useUpdatePreferences } from './usePreferences';

export type ThemePreference = 'light' | 'dark' | 'system';

// This literal is duplicated in the frontend/index.html pre-paint script. The
// two must change together.
// It is NOT `ms-theme`: that key belongs to the MKS-493 visual theme (Sparkle).
export const THEME_STORAGE_KEY = 'ms-color-scheme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Absent or unknown values mean light, so nothing changes until a user chooses. */
export function normalizeTheme(value: unknown): ThemePreference {
  return value === 'dark' || value === 'system' ? value : 'light';
}

// Storage access is wrapped everywhere: private mode or blocked storage must
// never break the app.
export function readStoredTheme(): ThemePreference | null {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* storage unavailable: the class is still applied, only the pre-paint mirror is lost */
  }
}

function systemPrefersDark(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

function applyDarkClass(isDark: boolean): void {
  document.documentElement.classList.toggle('dark', isDark);
}

export function useThemeController(): void {
  const { data } = usePreferences();
  const prefsLoaded = data !== undefined;
  const theme = normalizeTheme(data?.colorScheme);

  useEffect(() => {
    // Preferences still on the wire: the pre-paint state stands.
    if (!prefsLoaded) return;

    writeStoredTheme(theme);
    applyDarkClass(theme === 'dark' || (theme === 'system' && systemPrefersDark()));

    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const listener = (event: { matches: boolean }) => applyDarkClass(event.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, [prefsLoaded, theme]);
}

export function useThemeChoice(): {
  theme: ThemePreference;
  setTheme: (t: ThemePreference) => void;
  isSaving: boolean;
} {
  const { data } = usePreferences();
  const update = useUpdatePreferences();

  // Until the preferences query lands, the stored key (written by the last
  // choice) keeps the menu on the option the pre-paint script already applied.
  const theme = data !== undefined ? normalizeTheme(data.colorScheme) : (readStoredTheme() ?? 'light');

  return {
    theme,
    setTheme: (t) => update.mutate({ colorScheme: t }),
    isSaving: update.isPending,
  };
}
